import { useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import { mutationErrorMessage } from '../../lib/mutation-error';
import AssignReservationDialog from './AssignReservationDialog';
import BookingImportDialog from './BookingImportDialog';
import MarkBookedDialog from './MarkBookedDialog';
import SendConfirmationsDialog from './SendConfirmationsDialog';
import ReservationOccupantsDialog from './reservations/ReservationOccupantsDialog';
import ReservationRow from './reservations/ReservationRow';
import type {
  AssignReservationRequest,
  GroupDetail as GroupDetailType,
  GroupRoomRequest,
  RoomRequestStatusExtras,
} from '../../types/groups';
import { resolveTripTimeZone } from '../../lib/tripTime';

export default function ReservationsTab({ group, onChanged }: { group: GroupDetailType; onChanged: () => void }) {
  const statusMutation = useMutation(({ requestId, status, extras }: { requestId: string; status: string; extras?: RoomRequestStatusExtras }) => groupsApi.updateRoomRequestStatus(group.id, requestId, status, extras), { onSuccess: onChanged });
  const assignMutation = useMutation((body: AssignReservationRequest) => groupsApi.assignReservation(group.id, body), { onSuccess: onChanged });
  const sendConfirmationMutation = useMutation((requestId: string) => groupsApi.sendRoomConfirmation(group.id, requestId), { onSuccess: onChanged });
  const invoiceMutation = useMutation((requestId: string) => groupsApi.markManualInvoice(group.id, requestId, { invoiceNotes: 'Manual invoice sent outside Tripsby.' }), { onSuccess: onChanged });
  const assigning = assignMutation.isLoading;
  const error = [statusMutation, assignMutation, sendConfirmationMutation, invoiceMutation]
    .map((mutation) => mutationErrorMessage(mutation.error))
    .find(Boolean) || '';

  const [bookingRequest, setBookingRequest] = useState<GroupRoomRequest | null>(null);
  // Occupants are their own resource, not part of the group payload — one query
  // per opened reservation rather than one per row on every render of the tab.
  const [occupantsRequest, setOccupantsRequest] = useState<GroupRoomRequest | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const bookedRequests = useMemo(() => group.roomRequests.filter((r) => r.status === 'booked'), [group.roomRequests]);
  const bookedIds = useMemo(() => new Set(bookedRequests.map((r) => r.id)), [bookedRequests]);
  // Only ever count/send selections that are still booked. If a checked reservation's
  // status changes (or it leaves the list) after selection, drop it here so the "(N)"
  // counter and what actually gets sent stay in sync with the checkboxes on screen —
  // stale ids left in `selected` are inert. Memoized so the array handed to the dialog
  // is stable and doesn't re-fire its dry-run on unrelated re-renders (e.g. window focus).
  const selectedBookedIds = useMemo(() => [...selected].filter((id) => bookedIds.has(id)), [selected, bookedIds]);
  const allBookedSelected = bookedRequests.length > 0 && bookedRequests.every((r) => selected.has(r.id));
  const toggleSelect = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelected(allBookedSelected ? new Set() : new Set(bookedRequests.map((r) => r.id)));
  const blockFor = (request: GroupRoomRequest) => group.roomBlocks.find((b) => b.id === request.roomBlockId);
  const blockName = (request: GroupRoomRequest) => {
    const block = blockFor(request);
    return block ? `${block.hotelName} · ${block.roomType}` : request.roomBlockId;
  };
  const handleStatus = (request: GroupRoomRequest, status: string) => {
    // Booking collects confirmation + rate in a dialog before firing the update.
    if (status === 'booked') {
      setBookingRequest(request);
      return;
    }
    statusMutation.mutate({ requestId: request.id, status });
  };
  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {bookedRequests.length > 0 && (
            <FormControlLabel
              control={<Checkbox size="small" checked={allBookedSelected} indeterminate={selectedBookedIds.length > 0 && !allBookedSelected} onChange={toggleSelectAll} />}
              label={<Typography variant="body2" color="text.secondary">Select all booked</Typography>}
            />
          )}
          <Button variant="text" onClick={() => setSendOpen(true)} disabled={!selectedBookedIds.length}>
            {selectedBookedIds.length ? `Send confirmation (${selectedBookedIds.length})` : 'Send confirmation'}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setImportOpen(true)} disabled={!group.roomBlocks.length}>
            Import a booking
          </Button>
          <Button variant="contained" onClick={() => setAssignOpen(true)} disabled={!group.invitees.length || !group.roomBlocks.length}>
            Assign a reservation
          </Button>
        </Stack>
      </Box>
      {group.roomRequests.map((request) => (
        <ReservationRow
          key={request.id}
          request={request}
          timeZone={resolveTripTimeZone(group.timeZone).timeZone}
          blockLabel={blockName(request)}
          selected={selected.has(request.id)}
          showSelect={request.status === 'booked'}
          onToggleSelect={() => toggleSelect(request.id)}
          onStatusChange={(status) => handleStatus(request, status)}
          // The second door onto the booking dialog, and the reason it is now
          // reachable at all: same setter as the transition above, so both
          // entries end on the identical onConfirm below.
          onEditBooking={() => setBookingRequest(request)}
          onManageOccupants={() => setOccupantsRequest(request)}
          onInvoice={() => invoiceMutation.mutate(request.id)}
          onSendConfirmation={() => sendConfirmationMutation.mutate(request.id)}
          sendingConfirmation={sendConfirmationMutation.isLoading}
        />
      ))}
      {!group.roomRequests.length ? <Alert severity="info">No reservations yet. Assign one above, or wait for guests to request rooms.</Alert> : null}
      {assignOpen ? (
        <AssignReservationDialog
          invitees={group.invitees}
          roomBlocks={group.roomBlocks}
          saving={assigning}
          onClose={() => setAssignOpen(false)}
          onAssign={(body) => {
            assignMutation.mutate(body);
            setAssignOpen(false);
          }}
        />
      ) : null}
      {importOpen ? (
        <BookingImportDialog
          invitees={group.invitees}
          roomBlocks={group.roomBlocks}
          saving={assigning}
          onClose={() => setImportOpen(false)}
          onAssign={(body) => {
            // Keep the dialog open until the assign succeeds — a failed submit
            // must not discard the (paid) extraction + the host's edits.
            assignMutation.mutate(body, { onSuccess: () => setImportOpen(false) });
          }}
        />
      ) : null}
      {bookingRequest ? (
        <MarkBookedDialog
          request={bookingRequest}
          block={blockFor(bookingRequest)}
          saving={statusMutation.isLoading}
          onClose={() => setBookingRequest(null)}
          onConfirm={(extras) => {
            // Same deferral as the import path above, and it matters more here:
            // this dialog is also the EDIT path for a booked reservation, so
            // closing before the PATCH lands would throw away a retyped
            // confirmation number and five rate fields while the stale amounts
            // stay live on the guest's trip page. Stay open until it succeeds.
            statusMutation.mutate(
              { requestId: bookingRequest.id, status: 'booked', extras },
              { onSuccess: () => setBookingRequest(null) },
            );
          }}
        />
      ) : null}
      {occupantsRequest ? (
        <ReservationOccupantsDialog
          groupId={group.id}
          request={occupantsRequest}
          blockLabel={blockName(occupantsRequest)}
          invitees={group.invitees}
          onClose={() => setOccupantsRequest(null)}
          onChanged={onChanged}
        />
      ) : null}
      {sendOpen ? (
        <SendConfirmationsDialog
          groupId={group.id}
          requestIds={selectedBookedIds}
          onClose={() => setSendOpen(false)}
          onSent={() => { setSelected(new Set()); onChanged(); }}
        />
      ) : null}
    </Stack>
  );
}
